import dayjs from 'dayjs';
import api from '../../utils/api';

export const API_DATETIME = 'YYYY-MM-DD HH:mm:ss';

export const OFFER_TYPES = [
  { value: 'discount', label: 'Discount' },
  { value: 'item', label: 'Item Offer' },
  { value: 'category', label: 'Category Offer' }
];

export const getOfferType = (value) =>
  OFFER_TYPES.find((t) => t.value === value) || {
    value,
    label: value ? String(value).replace(/_/g, ' ') : '—'
  };

export const emptyForm = () => ({
  store_id: '',
  offer_name_en: '',
  offer_type: '',
  offer_value_type: 'percentage',
  offer_value: '',
  from_datetime: null,
  to_datetime: null,
  line_ids: [],
  line_seed_options: [],
  accepted_payments: ["card", "cod", "wallet"],
  usage_limit_per_user: "",
  total_usage_limit: "",
  minimum_purchase_amount: "",
  status: true,
  auto_apply: false,
  is_first_order: false,
  coupon_code: "",
  image_en: null,
  image_ar: null,
  image_en_url: "",
  image_ar_url: "",
  description: "",
  _duration_preset: "custom"
});

export function formatApiDatetime(value) {
  if (!value) return null;
  return dayjs(value).format(API_DATETIME);
}

export function getStoreId(store) {
  return store?.store_id ?? store?.id ?? '';
}

export function getProductId(product) {
  return product?.id ?? product?.product_id ?? '';
}

export function getCategoryId(category) {
  return category?.id ?? category?.category_id ?? '';
}

export function buildOfferEditPayload(form) {
  const payload = {
    from_datetime: formatApiDatetime(form.from_datetime),
    to_datetime: formatApiDatetime(form.to_datetime)
  };

  if (form.offer_type === 'item') {
    payload.lines = form.line_ids.map((product_id) => ({ product_id }));
  } else if (form.offer_type === 'category') {
    payload.lines = form.line_ids.map((category_id) => ({ category_id }));
  }

  return payload;
}

export function buildOfferPayload(form) {
  const payload = {
    store_id: form.store_id,
    offer_name_en: form.offer_name_en.trim(),
    offer_type: form.offer_type,
    offer_value_type: form.offer_value_type,
    offer_value: parseFloat(form.offer_value) || 0,
    from_datetime: formatApiDatetime(form.from_datetime),
    to_datetime: formatApiDatetime(form.to_datetime)
  };

  if (form.offer_type === 'item') {
    payload.lines = form.line_ids.map((product_id) => ({ product_id }));
  } else if (form.offer_type === 'category') {
    payload.lines = form.line_ids.map((category_id) => ({ category_id }));
  }

  return payload;
}

export function getOfferLines(data) {
  if (Array.isArray(data?.offer_lines)) return data.offer_lines;
  if (Array.isArray(data?.lines)) return data.lines;
  return [];
}

function readStoreIdFromRecord(record) {
  if (!record || typeof record !== 'object') return '';
  const direct = record.store_id ?? record.storeId ?? record.store_uuid;
  if (direct) return String(direct);
  const store = record.Store ?? record.store;
  if (store) {
    const id = store.store_id ?? store.id ?? store.storeId;
    if (id) return String(id);
  }
  return '';
}

export function getStoreIdFromOffer(d) {
  if (!d || typeof d !== 'object') return '';

  const direct = readStoreIdFromRecord(d);
  if (direct) return direct;

  const lines = getOfferLines(d);
  for (const line of lines) {
    const fromCategory = readStoreIdFromRecord(line.category);
    if (fromCategory) return fromCategory;

    const fromProduct = readStoreIdFromRecord(line.product);
    if (fromProduct) return fromProduct;

    const productStore = line.product?.store_id ?? line.product?.Store?.store_id;
    if (productStore) return String(productStore);
  }

  return '';
}

/** Resolve store when offer payload omits store_id (fetch category/product like create flow). */
export async function resolveStoreIdForOffer(d, listOffer = null) {
  const fromList = getStoreIdFromOffer(listOffer);
  if (fromList) return fromList;

  const fromOffer = getStoreIdFromOffer(d);
  if (fromOffer) return fromOffer;

  const lines = getOfferLines(d);
  const offerType = normalizeOfferType(d?.offer_type, d);

  if (offerType === 'category') {
    for (const line of lines) {
      const categoryId = line.category?.id ?? line.category_id;
      if (!categoryId) continue;
      try {
        const res = await api.get(`/product/category/${categoryId}`);
        const data = res.data?.data ?? res.data;
        const sid = readStoreIdFromRecord(data);
        if (sid) return sid;
      } catch (e) {
        console.error('Failed to resolve store from category', categoryId, e);
      }
    }
  }

  if (offerType === 'item') {
    for (const line of lines) {
      const productId = line.product?.id ?? line.product_id;
      if (!productId) continue;
      try {
        const res = await api.get(`/product/${productId}`);
        const data = res.data?.data ?? res.data;
        const sid = readStoreIdFromRecord(data);
        if (sid) return sid;
      } catch (e) {
        console.error('Failed to resolve store from product', productId, e);
      }
    }
  }

  return '';
}

export function normalizeOfferType(value, data) {
  const v = String(value || '').toLowerCase();
  if (v === 'item' || v.includes('item') || v.includes('product')) return 'item';
  if (v === 'category' || v.includes('categor')) return 'category';
  if (v === 'discount' || v.includes('discount')) return 'discount';
  const lines = getOfferLines(data);
  if (lines.some((l) => l.product || l.product_id)) return 'item';
  if (lines.some((l) => l.category || l.category_id)) return 'category';
  return value || '';
}

export function parseLinesFromOffer(data) {
  const lines = getOfferLines(data);
  const offerType = normalizeOfferType(data?.offer_type, data);

  if (offerType === 'item') {
    return lines.map((l) => l.product?.id ?? l.product_id).filter(Boolean);
  }
  if (offerType === 'category') {
    return lines.map((l) => l.category?.id ?? l.category_id).filter(Boolean);
  }
  return [];
}

export function parseLineSeedFromOffer(data) {
  const lines = getOfferLines(data);
  const offerType = normalizeOfferType(data?.offer_type, data);

  if (offerType === 'item') {
    return lines
      .map((l) => l.product ?? (l.product_id ? { id: l.product_id } : null))
      .filter(Boolean)
      .map((p) => {
        const id = p.id ?? p.product_id;
        return {
          id,
          product_id: id,
          name: p.name,
          name_en: p.name_en || p.name || p.title_en || `Product #${id}`
        };
      });
  }
  if (offerType === 'category') {
    return lines
      .map((l) => l.category ?? (l.category_id ? { id: l.category_id } : null))
      .filter(Boolean)
      .map((c) => {
        const id = c.id ?? c.category_id;
        return {
          id,
          category_id: id,
          name_en: c.name_en || c.name_ar || c.category_name_en || `Category #${id}`
        };
      });
  }
  return [];
}

export async function buildFormFromOffer(d, listOffer = null) {
  const store_id = (await resolveStoreIdForOffer(d, listOffer)) || getStoreIdFromOffer(d);
  return {
    store_id,
    offer_name_en: d.offer_name_en || '',
    offer_type: normalizeOfferType(d.offer_type, d),
    offer_value_type: d.offer_value_type || 'percentage',
    offer_value: d.offer_value ?? '',
    from_datetime: d.from_datetime ? dayjs(d.from_datetime, API_DATETIME) : null,
    to_datetime: d.to_datetime ? dayjs(d.to_datetime, API_DATETIME) : null,
    line_ids: parseLinesFromOffer(d),
    line_seed_options: parseLineSeedFromOffer(d),
    accepted_payments: d.accepted_payments ? (Array.isArray(d.accepted_payments) ? d.accepted_payments : JSON.parse(d.accepted_payments)) : ["card", "cod", "wallet"],
    usage_limit_per_user: d.usage_limit_per_user ?? '',
    total_usage_limit: d.total_usage_limit ?? '',
    minimum_purchase_amount: d.minimum_purchase_amount ?? '',
    status: d.status ?? true,
    auto_apply: d.auto_apply ?? false,
    is_first_order: d.is_first_order ?? false,
    coupon_code: d.coupon_code ?? '',
    image_en: null,
    image_ar: null,
    image_en_url: d.image_en || '',
    image_ar_url: d.image_ar || '',
    description: d.description || '',
    _duration_preset: "custom"
  };
}

export function formFromOfferData(d) {
  return {
    store_id: getStoreIdFromOffer(d),
    offer_name_en: d.offer_name_en || '',
    offer_type: normalizeOfferType(d.offer_type, d),
    offer_value_type: d.offer_value_type || 'percentage',
    offer_value: d.offer_value ?? '',
    from_datetime: d.from_datetime ? dayjs(d.from_datetime, API_DATETIME) : null,
    to_datetime: d.to_datetime ? dayjs(d.to_datetime, API_DATETIME) : null,
    line_ids: parseLinesFromOffer(d),
    line_seed_options: parseLineSeedFromOffer(d),
    accepted_payments: d.accepted_payments ? (Array.isArray(d.accepted_payments) ? d.accepted_payments : JSON.parse(d.accepted_payments)) : ["card", "cod", "wallet"],
    usage_limit_per_user: d.usage_limit_per_user ?? '',
    total_usage_limit: d.total_usage_limit ?? '',
    minimum_purchase_amount: d.minimum_purchase_amount ?? '',
    status: d.status ?? true,
    auto_apply: d.auto_apply ?? false,
    is_first_order: d.is_first_order ?? false,
    coupon_code: d.coupon_code ?? '',
    image_en: null,
    image_ar: null,
    image_en_url: d.image_en || '',
    image_ar_url: d.image_ar || '',
    description: d.description || '',
    _duration_preset: "custom"
  };
}

export function lineIdsInclude(lineIds, id) {
  if (!id) return false;
  const key = String(id);
  return lineIds.some((x) => String(x) === key);
}

export function mergeTargetOptions(seed = [], fetched = [], getId) {
  const seen = new Set();
  const merged = [];
  for (const o of [...seed, ...fetched]) {
    const id = getId(o);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    merged.push(o);
  }
  return merged;
}
